import { useQuery } from "@tanstack/react-query";
import { AppLink, Overline, SegmentedFilter } from "@amber/ui";
import type { SourceRow } from "../../contracts/index";
import { sourcesQuery } from "../queries";
import { CoverageTimeline, HealthMark, SourceHealthCell, TimelineAxis, shortDate } from "../sources/SourceVisuals";

export type CoverageWindow = 30 | 90 | 180;

const WINDOWS: Record<string, CoverageWindow> = { "30": 30, "90": 90, "180": 180 };
const GRID =
  "grid grid-cols-[minmax(150px,230px)_minmax(0,1fr)_120px] items-center gap-x-3.5 max-compact:grid-cols-[minmax(96px,140px)_minmax(0,1fr)_128px]";

function CompleteThrough({ row, extending }: { row: SourceRow; extending: boolean }) {
  return (
    <span className="flex flex-col items-start gap-0.5">
      <span className="font-mono text-md text-fg">{shortDate(row.complete_through)}</span>
      {extending && row.complete_back_to ? (
        <span className="text-sm text-muted">back to {shortDate(row.complete_back_to)}</span>
      ) : null}
      {row.health.reason ? (
        <span className="text-sm">
          <SourceHealthCell row={row} />
        </span>
      ) : null}
    </span>
  );
}

function CoverageRows({
  rows,
  from,
  to,
  extending,
}: {
  rows: readonly SourceRow[];
  from: string;
  to: string;
  extending: boolean;
}) {
  return (
    <div className={`${GRID} mt-2`}>
      <span className="border-b border-divider pb-1 text-sm text-muted">Source</span>
      <span className="border-b border-divider pb-1">
        <TimelineAxis from={from} to={to} />
      </span>
      <span className="border-b border-divider pb-1 text-sm text-muted">Complete through</span>
      {rows.map((r) => (
        <div key={r.id} className="contents">
          <span className="flex min-w-0 items-center gap-2 py-2">
            <HealthMark state={r.health.state} />
            <span className="sr-only">{r.health.label}:</span>
            <AppLink href={`/sources/${r.id}`} className="truncate" title={`${r.title} · ${r.platform.label}`}>
              {r.title}
            </AppLink>
          </span>
          <span className="py-2">
            <CoverageTimeline row={r} from={from} to={to} />
          </span>
          <span className="py-2 text-md">
            <CompleteThrough row={r} extending={extending} />
          </span>
        </div>
      ))}
    </div>
  );
}

/** The Sources coverage timeline for the sources in one person's plan, on one shared axis. */
export function ProfileCoverage({
  profileId,
  window,
  onWindow,
  empty,
}: {
  profileId: string;
  window: CoverageWindow;
  onWindow: (window: CoverageWindow) => void;
  empty: string | null;
}) {
  const data = useQuery({ ...sourcesQuery({ person: profileId, window }), enabled: empty === null }).data;
  const rows = (data?.sources ?? []).filter(
    (s) => s.platform.collected && s.people.some((p) => p.id === profileId && p.in_plan),
  );
  const gaps = rows.reduce((n, r) => n + r.open_gaps, 0);
  return (
    <section aria-label="Coverage" className="mt-3 rounded-xl border border-divider px-4 py-3 text-ui">
      <div className="flex flex-wrap items-center gap-3">
        <Overline className="m-0 flex-1">
          Coverage{" "}
          {empty === null ? (
            <span className="font-normal normal-case tracking-normal">
              · last {window} days · {rows.length} source{rows.length === 1 ? "" : "s"}
              {gaps > 0 ? ` · ${String(gaps)} open gap${gaps === 1 ? "" : "s"}` : ""}
            </span>
          ) : null}
        </Overline>
        {empty === null ? (
          <SegmentedFilter
            label="Coverage window"
            value={String(window)}
            options={[
              { value: "30", label: "30 d" },
              { value: "90", label: "90 d" },
              { value: "180", label: "180 d" },
            ]}
            onChange={(v) => {
              onWindow(WINDOWS[v] ?? 90);
            }}
          />
        ) : null}
      </div>
      {empty !== null ? <p className="m-0 mt-1 text-md text-muted">{empty}</p> : null}
      {empty === null && data && rows.length === 0 ? (
        <p className="m-0 mt-1 text-md text-muted">No source in this person&apos;s plan is collected yet.</p>
      ) : null}
      {empty === null && data && rows.length > 0 ? (
        <CoverageRows rows={rows} from={data.window.from} to={data.window.to} extending={data.extension !== null} />
      ) : null}
    </section>
  );
}
